import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Form, List, Message, Modal,
} from 'semantic-ui-react';

import ReasonFields, { reasonValidationError } from './ReasonFields';
import {
  DIALOG_CANCEL_LABEL,
  OPT_OUT_CONFIRM_LABEL,
  OPT_OUT_DIALOG_HEADER,
  OPT_OUT_NO_SUBSCRIPTION_NOTICE,
} from '../../constants/onboardingQueue';
import { dealerLabel } from '../../utilities/onboardingQueue';

const OptOutDialog = ({
  record, dealers, loading, errorMessage, onClose, onConfirm,
}) => {
  const [reason, setReason] = useState(null);
  const [otherText, setOtherText] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleConfirm = () => {
    const invalid = reasonValidationError(reason, otherText);
    setValidationError(invalid);
    if (invalid) return;
    onConfirm({ reason, otherText: otherText.trim() });
  };

  return (
    <Modal
      open
      size='small'
      onClose={handleClose}
      closeOnEscape={!loading}
      closeOnDimmerClick={false}
      data-testid='queue-opt-out-dialog'
    >
      <Modal.Header>{OPT_OUT_DIALOG_HEADER}</Modal.Header>
      <Modal.Content>
        {dealers ? (
          <List bulleted data-testid='queue-opt-out-dealers'>
            {dealers.map(dealer => (
              <List.Item key={dealer.flatRateOfferId} data-testid='queue-opt-out-dealers-item'>
                {dealerLabel(dealer)}
              </List.Item>
            ))}
          </List>
        ) : (
          <p data-testid='queue-opt-out-dealer'>{record.dealerName}</p>
        )}

        <Message info data-testid='queue-opt-out-no-subscription'>
          <p>{OPT_OUT_NO_SUBSCRIPTION_NOTICE}</p>
        </Message>

        <Form>
          <ReasonFields
            reason={reason}
            otherText={otherText}
            disabled={loading}
            invalid={Boolean(validationError)}
            onReasonChange={value => {
              setReason(value);
              setValidationError('');
            }}
            onOtherTextChange={value => {
              setOtherText(value);
              setValidationError('');
            }}
          />
        </Form>

        {validationError && (
          <Message negative size='mini' data-testid='queue-opt-out-validation'>
            <p>{validationError}</p>
          </Message>
        )}

        {errorMessage && (
          <Message negative data-testid='queue-opt-out-error'>
            <p>{errorMessage}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={loading} data-testid='queue-opt-out-cancel'>
          {DIALOG_CANCEL_LABEL}
        </Button>
        <Button
          primary
          loading={loading}
          disabled={loading}
          data-testid='queue-opt-out-confirm'
          onClick={handleConfirm}
        >
          {OPT_OUT_CONFIRM_LABEL}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

OptOutDialog.propTypes = {
  record: PropTypes.shape({ dealerName: PropTypes.string }).isRequired,
  dealers: PropTypes.arrayOf(PropTypes.shape({
    flatRateOfferId: PropTypes.string,
    dealerName: PropTypes.string,
    customerId: PropTypes.string,
  })),
  loading: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

OptOutDialog.defaultProps = {
  dealers: null,
  loading: false,
  errorMessage: null,
};

export default OptOutDialog;
